import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  TextField,
  Divider,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  useGetRmaByIdQuery, 
  useUpdateRmaMutation 
} from '../../../store/api/rmaApi';

const STATUS_COLORS = {
  pending_dispatch: 'warning',
  dispatched: 'info',
  under_repair: 'secondary',
  replaced: 'success',
  credit_issued: 'primary',
  closed: 'default',
};

const STATUS_LABELS = {
  pending_dispatch: 'Pending Dispatch',
  dispatched: 'Dispatched',
  under_repair: 'Under Repair',
  replaced: 'Replaced',
  credit_issued: 'Credit Issued',
  closed: 'Closed',
};

export default function RmaView() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data: rma, isLoading } = useGetRmaByIdQuery(id);
  const [updateRma, { isLoading: isUpdating }] = useUpdateRmaMutation();
  
  const [status, setStatus] = useState('pending_dispatch');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [newProductImei, setNewProductImei] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (rma) {
      setStatus(rma.status || 'pending_dispatch');
      setResolutionNotes(rma.resolutionNotes || '');
    }
  }, [rma]);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (!rma) return <Typography>RMA ticket not found.</Typography>;

  const handleUpdate = async () => {
    setErrorMsg('');
    if (status === 'replaced' && !newProductImei && rma.status !== 'replaced') {
      setErrorMsg('You must provide the new replacement IMEI when marking as replaced.');
      return;
    }
    try {
      await updateRma({
        id,
        status,
        resolutionNotes,
        newProductImei: status === 'replaced' ? newProductImei : undefined
      }).unwrap();
    } catch (err) {
      setErrorMsg(err.data?.message || 'Failed to update RMA');
    }
  };

  const isClosed = rma.status === 'closed';
  const showImeiField = status === 'replaced' && rma.status !== 'replaced';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {rma.rmaNumber}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/rma')}>
          Back to List
        </Button>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{xs:12, md:5}}>
          <Paper sx={{ p: 3, mb: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Current Status</Typography>
              <Chip 
                label={STATUS_LABELS[rma.status]} 
                color={STATUS_COLORS[rma.status]} 
              />
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Device Info</Typography>
            <Typography variant="body1"><strong>Product:</strong> {rma.productImei?.product?.name}</Typography>
            <Typography variant="body1"><strong>Defective IMEI:</strong> {rma.productImei?.imei}</Typography>
            <Typography variant="body1"><strong>SKU:</strong> {rma.productImei?.product?.sku}</Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Supplier / Brand</Typography>
            <Typography variant="body1"><strong>Name:</strong> {rma.supplier?.name || 'N/A'}</Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Defect Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {rma.defectDescription}
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{xs:12, md:7}}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Update Status & Resolution</Typography>
            
            <Grid container spacing={3}>
              <Grid size={{xs:12}}>
                <FormControl fullWidth disabled={isClosed}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="pending_dispatch">Pending Dispatch</MenuItem>
                    <MenuItem value="dispatched">Dispatched to Supplier</MenuItem>
                    <MenuItem value="under_repair">Under Repair</MenuItem>
                    <MenuItem value="replaced">Replaced (New IMEI)</MenuItem>
                    <MenuItem value="credit_issued">Credit Issued</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {showImeiField && (
                <Grid size={{xs:12}}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Marking this RMA as Replaced will mark the old IMEI as returned and add the new IMEI below into your inventory.
                  </Alert>
                  <TextField
                    fullWidth
                    label="New Replacement IMEI"
                    value={newProductImei}
                    onChange={(e) => setNewProductImei(e.target.value)}
                    required
                  />
                </Grid>
              )}

              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Resolution / Update Notes"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  disabled={isClosed}
                  placeholder="e.g. Tracking number, repair estimate, credit note reference..."
                />
              </Grid>

              {!isClosed && (
                <Grid size={{xs:12}}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                      variant="contained" 
                      onClick={handleUpdate}
                      disabled={isUpdating}
                    >
                      Update RMA
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
